"use client";

import Image from "next/image";
import Link from "next/link";
import { type RefObject, type ReactNode, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

function useVisible<T extends HTMLElement = HTMLDivElement>(threshold = 0.1): [RefObject<T>, boolean] {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    const element = ref.current;
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, [threshold]);

  return [ref, visible] as const;
}

function getDelayClass(delay: number) {
  if (delay <= 0) return "delay-0";
  if (delay < 0.1) return "delay-75";
  if (delay < 0.16) return "delay-150";
  if (delay < 0.22) return "delay-200";
  return "delay-300";
}

function Reveal({
  children,
  delay = 0,
  from = "bottom",
}: {
  children: ReactNode;
  delay?: number;
  from?: "bottom" | "left" | "right";
}) {
  const [ref, visible] = useVisible();

  const fromClasses = {
    bottom: "translate-y-8",
    left: "-translate-x-8",
    right: "translate-x-8",
  } as const;

  return (
    <div
      ref={ref}
      className={cn(
        "opacity-0",
        fromClasses[from],
        "transition-all duration-[650ms] ease-out",
        getDelayClass(delay),
        visible && "opacity-100 translate-x-0 translate-y-0"
      )}
    >
      {children}
    </div>
  );
}

type Faq = {
  q: string;
  a: string;
};

type Loan = {
  title: string;
  best: string;
  feature: string;
};

type StepData = {
  n: string;
  title: string;
  body: string;
};

const LOAN_TYPES: Loan[] = [
  {
    title: "Term Loans",
    best: "Business expansion, equipment, infrastructure",
    feature: "Long-term repayment up to 10 years",
  },
  {
    title: "Working Capital Loans",
    best: "Day-to-day operations, inventory, payroll",
    feature: "Short-term, revolving credit options",
  },
  {
    title: "MSME Priority Lending",
    best: "MSME-registered businesses",
    feature: "Preferential interest rates + government support",
  },
  {
    title: "Collateral-Free Loans",
    best: "Businesses without fixed assets",
    feature: "CGTMSE-covered, no security required",
  },
  {
    title: "Project Financing",
    best: "Large-scale capital projects",
    feature: "Customised structure for complex financing",
  },
  {
    title: "Government Scheme Loans",
    best: "Eligible entrepreneurs & startups",
    feature: "MUDRA, Stand-Up India, CGTMSE access",
  },
  {
    title: "Startup Financing",
    best: "Early-stage and growth-stage startups",
    feature: "Flexible terms, lower documentation bar",
  },
  {
    title: "Business Expansion Loans",
    best: "Scaling into new markets",
    feature: "Higher loan amounts, longer tenures",
  },
];

const ELIGIBILITY = [
  "Minimum 3 years of business vintage (varies by lender and loan type)",
  "Annual turnover meeting lender-specified threshold for loan amount",
  "CIBIL score of 650 or above — higher scores attract better rates",
  "Positive cash flow demonstrated over 2+ years",
  "Valid business registration — Pvt Ltd, LLP, Partnership, Proprietorship",
  "PAN and TAN of the business and promoters",
  "GST registration and regular filing (if applicable)",
  "ITR filings for the previous 2–3 years",
  "Collateral or security if required by lender (not for CGTMSE-covered loans)",
];

const STEPS: StepData[] = [
  {
    n: "01",
    title: "Requirement Analysis",
    body: "We understand your business needs, loan amount and financing objectives to define the right product.",
  },
  {
    n: "02",
    title: "Eligibility Assessment",
    body: "We evaluate your financials, credit profile and documentation readiness against lender criteria.",
  },
  {
    n: "03",
    title: "Loan Structuring",
    body: "We design the optimal loan amount, tenure, moratorium and repayment plan for your situation.",
  },
  {
    n: "04",
    title: "Lender Matching",
    body: "We submit applications to the most suitable lenders from our network of banks and NBFCs.",
  },
  {
    n: "05",
    title: "Term Negotiation",
    body: "We negotiate interest rates, processing fees and conditions on your behalf to secure the best deal.",
  },
  {
    n: "06",
    title: "Disbursement Facilitation",
    body: "We assist with final documentation and ensure smooth, timely fund release to your account.",
  },
];

const DOCS = [
  "Business registration certificate — Pvt Ltd / LLP / Partnership / GST certificate",
  "Memorandum and Articles of Association (for companies)",
  "GST registration certificate and last 12 months of GSTR returns",
  "Audited financial statements for the past 2–3 financial years",
  "Income Tax Returns of the business and promoters (2–3 years)",
  "Bank statements of the business account (last 12 months)",
  "KYC documents: PAN, Aadhaar, passport-size photographs of all directors/partners",
  "Proof of business premises: owned (sale deed) or rented (rent agreement + NOC)",
  "Business profile or project report (for project/expansion loans)",
  "Existing loan statements and sanction letters (if applicable)",
  "Collateral documents: property papers, valuation report (if applicable)",
];

const FAQS: Faq[] = [
  {
    q: "What is the minimum loan amount you facilitate?",
    a: "We facilitate loans starting from ₹5 lakh up to ₹50 crore depending on the business profile and lender.",
  },
  {
    q: "How long does loan processing take?",
    a: "Typically 7–21 working days from complete document submission, depending on the lender and loan type.",
  },
  {
    q: "Do you charge for loan facilitation?",
    a: "We charge a nominal facilitation fee only upon successful loan disbursement. No upfront charges.",
  },
  {
    q: "Can I get a loan if my CIBIL score is low?",
    a: "Some NBFC lenders and government schemes accept lower CIBIL scores. We assess the best available options for your profile.",
  },
  {
    q: "What is a collateral-free loan?",
    a: "Under the CGTMSE scheme, the government provides a guarantee to lenders, allowing businesses to access loans without pledging assets.",
  },
];

function Divider({ className }: { className?: string }) {
  return <div className={cn("w-[44px] h-[3px] bg-[#1565C0] mb-6", className)} />;
}

function FaqItem({ q, a, i }: { q: string; a: string; i: number }) {
  const [open, setOpen] = useState(false);
  const [ref, visible] = useVisible();
  const delayClass = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300"][Math.min(i, 4)];

  return (
    <div
      ref={ref}
      className={cn(
        "border-b border-[#E2E8F0] transition-all duration-500 ease-out",
        delayClass,
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      )}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="w-full bg-transparent border-none py-5 flex items-center justify-between gap-4 text-left"
      >
        <span className="text-[15px] font-semibold text-[#0F172A] leading-[1.5]">{q}</span>
        <span
          className={cn(
            "w-7 h-7 rounded-full flex items-center justify-center transition-colors duration-200",
            open ? "bg-[#1565C0] text-white" : "bg-[#F8FAFC] text-[#1565C0]"
          )}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            {open ? (
              <path d="M2 6h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            ) : (
              <>
                <path d="M6 2v8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <path d="M2 6h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </>
            )}
          </svg>
        </span>
      </button>

      {open && <p className="pb-5 text-[14px] leading-[1.85] text-[#64748B]">{a}</p>}
    </div>
  );
}

function LoanRow({ loan, i }: { loan: Loan; i: number }) {
  const [ref, visible] = useVisible<HTMLTableRowElement>();
  const delayClass = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300"][Math.min(i, 4)];

  return (
    <tr
      ref={ref}
      className={cn(
        "group transition-all duration-500 ease-out hover:bg-[#F0F7FF] cursor-default",
        delayClass,
        visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4",
        i % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"
      )}
    >
      <td className="px-5 py-4 align-top font-semibold text-[#0F172A] text-[14px] leading-[1.4]">
        <div className="flex items-start gap-3">
          <span className="mt-2 inline-block h-2 w-2 rounded-full bg-[#1565C0] shrink-0" />
          {loan.title}
        </div>
      </td>
      <td className="px-5 py-4 align-top text-[13.5px] text-[#334155] leading-[1.5]">{loan.best}</td>
      <td className="px-5 py-4 align-top text-[13.5px] text-[#64748B] leading-[1.5]">{loan.feature}</td>
    </tr>
  );
}

function Step({ s, i, last }: { s: StepData; i: number; last?: boolean }) {
  const [ref, visible] = useVisible();
  const delayClass = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300"][Math.min(i, 4)];

  return (
    <div
      ref={ref}
      className={cn(
        "relative flex gap-4 pb-8 transition-all duration-500 ease-out",
        delayClass,
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
      )}
    >
      {!last && <span className="absolute left-[17px] top-[36px] bottom-0 w-px bg-[#E2E8F0]" />}
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1565C0] shrink-0">
        <span className="text-[11px] font-semibold text-white">{s.n}</span>
      </div>
      <div className="pt-[6px]">
        <div className="text-[14px] font-semibold text-[#0F172A] mb-1.5">{s.title}</div>
        <p className="text-[13.5px] leading-[1.75] text-[#64748B]">{s.body}</p>
      </div>
    </div>
  );
}

function DocItem({ doc, i }: { doc: string; i: number }) {
  const [ref, visible] = useVisible();
  const delayClass = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300"][Math.min(i, 4)];

  return (
    <div
      ref={ref}
      className={cn(
        "flex items-start gap-3 transition-all duration-500 ease-out",
        delayClass,
        visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
      )}
    >
      <span className="mt-2 inline-block h-2.5 w-2.5 rounded-full bg-[#1565C0] shrink-0" />
      <span className="text-[13.5px] leading-[1.7] text-[#334155]">{doc}</span>
    </div>
  );
}

export default function LoansPage() {
  return (
    <main className="bg-white text-[#0F172A]">
      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-0 lg:min-h-[580px]">
        <div className="flex flex-col justify-center bg-white py-[30px] pr-[30px] pl-4 sm:pl-8 lg:pl-[80px]">
          <div className="flex flex-wrap items-center gap-2 text-[12px] text-[#64748B] mb-6">
            <Link href="/services" className="text-[#1565C0] font-medium hover:underline">
              Services
            </Link>
            <span className="text-[#E2E8F0]">›</span>
            <Link href="/services/funding" className="text-[#1565C0] font-medium hover:underline">
              Funding
            </Link>
            <span className="text-[#E2E8F0]">›</span>
            <span>Business Loans</span>
          </div>

          <div className="flex flex-wrap gap-2 mb-5">
            {[
              "Term Loans",
              "Working Capital",
              "MSME Schemes",
              "Collateral-Free",
            ].map((tag) => (
              <span
                key={tag}
                className="text-[11px] font-semibold text-[#1565C0] bg-[#EFF6FF] py-1 px-2.5 tracking-[0.04em] rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>

          <span className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0] mb-3.5">
            Funding Assistance — Loans
          </span>
          <Divider />
          <h1 className="text-[clamp(26px,3.5vw,44px)] font-extrabold leading-[1.1] mb-6 max-w-[38rem]">
            Business Loan Solutions Tailored to Every Growth Stage
          </h1>
          <p className="text-[16px] leading-[1.85] text-[#64748B] mb-9 ">
            From working capital to large-scale expansion — we match your business with the right loan product, at the best terms, with complete documentation support from application to disbursement.
          </p>
          <div className="flex flex-wrap gap-3">
            <button className="inline-flex items-center justify-center rounded-sm bg-[#1565C0] text-white px-7 py-3 text-[13px] font-bold tracking-[0.06em]">
              CHECK ELIGIBILITY
            </button>
            <button className="inline-flex items-center justify-center rounded-sm border border-[#E2E8F0] bg-transparent text-[#0F172A] px-7 py-3 text-[13px] font-semibold tracking-[0.06em]">
              TALK TO A SPECIALIST
            </button>
          </div>
        </div>

        <div className="relative overflow-hidden w-full min-h-[250px] sm:min-h-[340px] lg:min-h-[420px]">
          <Image
            src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=900&q=85"
            alt="Business loans"
            fill
            className="object-cover"
          />
        </div>
      </section>

      <div className="bg-[#1565C0] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { v: "₹5L – 50Cr", l: "Loan Range" },
          { v: "8", l: "Loan Products" },
          { v: "7–21 Days", l: "Avg. Processing" },
          { v: "No Fee", l: "Until Disbursal" },
        ].map((item, index) => (
          <div
            key={index}
            className={cn(
              "py-5 text-center",
              index < 3 ? "lg:border-r border-white/15" : ""
            )}
          >
            <div className="text-[22px] font-black text-white leading-none">{item.v}</div>
            <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-white/70 mt-1">{item.l}</div>
          </div>
        ))}
      </div>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="mb-10 lg:mb-14">
              <span className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0] mb-3.5">
                Loan Types We Facilitate
              </span>
              <Divider />
              <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
                <h2 className="text-[clamp(24px,3vw,38px)] font-bold leading-[1.18]">
                  8 Loan Products. One Trusted Partner.
                </h2>
                <p className="text-[14px] leading-[1.85] text-[#64748B] max-w-[360px]">
                  We work with banks, NBFCs and government schemes to find the right product for your profile.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal>
            <div className="overflow-x-auto">
              <table className="min-w-full border-separate border-spacing-0 text-[14px]">
                <thead className="bg-[#1565C0]">
                  <tr>
                    {['Loan Type', 'Best For', 'Key Feature'].map((heading) => (
                      <th
                        key={heading}
                        className="px-5 py-4 text-left text-[11px] font-semibold uppercase tracking-[0.1em] text-white whitespace-nowrap"
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {LOAN_TYPES.map((loan, index) => (
                    <LoanRow key={index} loan={loan} i={index} />
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-[#F8FAFC]">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-2 items-center">
          <Reveal from="left">
            <div className="relative">
              <div className="relative w-full h-[240px] sm:h-[340px] lg:h-[420px] overflow-hidden rounded-[22px]">
                <Image
                  src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=700&q=80"
                  alt="Eligibility"
                  fill
                  className="object-cover"
                />
              </div>
              <div className="hidden lg:block absolute -bottom-6 -right-6 w-[310px] rounded-[22px] border border-[#E2E8F0] bg-white p-6 shadow-[0_8px_32px_rgba(0,0,0,0.10)]">
                <div className="text-[30px] font-black text-[#1565C0] leading-none">₹50Cr</div>
                <div className="text-[11px] uppercase tracking-[0.06em] text-[#64748B] font-medium mt-1">Maximum Loan</div>
                <div className="text-[28px] font-black text-[#E65100] leading-none mt-4">650+</div>
                <div className="text-[11px] uppercase tracking-[0.06em] text-[#64748B] font-medium mt-1">Min. CIBIL Score</div>
              </div>
            </div>
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <span className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0] mb-3.5">
              Eligibility Requirements
            </span>
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold leading-[1.18] mb-7">
              Standard Eligibility Criteria
            </h2>
            <p className="text-[15px] leading-[1.85] text-[#64748B] mb-8">
              Requirements vary by lender and loan type. Our team assesses your eligibility across multiple lenders to find the best match for your profile.
            </p>
            <div className="space-y-3">
              {ELIGIBILITY.map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="mt-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#1565C0]">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1.5 5.5L3.8 7.8L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <span className="text-[13.5px] leading-[1.7] text-[#334155]">{item}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-2 items-start">
          <Reveal from="left">
            <span className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0] mb-3.5">
              Our Process
            </span>
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold leading-[1.18] mb-5">
              6-Step Loan Facilitation Process
            </h2>
            <p className="text-[15px] leading-[1.85] text-[#64748B] mb-10">
              A structured methodology that matches you with the right lender, negotiates the best terms and ensures smooth disbursement — end to end.
            </p>
            <div className="relative w-full h-[160px] sm:h-[200px] lg:h-[260px] overflow-hidden rounded-[22px]">
              <Image
                src="https://images.unsplash.com/photo-1560472355-536de3962603?w=700&q=80"
                alt="Process"
                fill
                className="object-cover"
              />
            </div>
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div>
              {STEPS.map((step, index) => (
                <Step key={step.n} s={step} i={index} last={index === STEPS.length - 1} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-[#F8FAFC]">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-2 items-center">
          <Reveal from="left">
            <span className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0] mb-3.5">
              Required Documentation
            </span>
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold leading-[1.18] mb-7">
              Common Documents for All Loan Types
            </h2>
            <p className="text-[15px] leading-[1.85] text-[#64748B] mb-8">
              Documentation requirements vary by loan type, lender and business structure. We advise you on the exact list during consultation.
            </p>
            <div className="space-y-3">
              {DOCS.map((doc, index) => (
                <DocItem key={index} doc={doc} i={index} />
              ))}
            </div>
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <div className="relative w-full h-[320px] sm:h-[440px] lg:h-[540px] overflow-hidden rounded-[22px]">
              <Image
                src="https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=700&q=80"
                alt="Documentation"
                fill
                className="object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-2 items-start">
          <Reveal from="left">
            <span className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0] mb-3.5">
              Frequently Asked Questions
            </span>
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold leading-[1.18] mb-6">
              Common Questions About Business Loans
            </h2>
            <p className="text-[15px] leading-[1.85] text-[#64748B] mb-9">
              Everything you need to know before applying for a business loan through Farsight Solutions.
            </p>
            <div className="relative w-full h-[160px] sm:h-[200px] lg:h-[260px] overflow-hidden rounded-[22px]">
              <Image
                src="https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=700&q=80"
                alt="Business loan FAQ"
                fill
                className="object-cover"
              />
            </div>
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div className="border-t border-[#E2E8F0] pt-8">
              {FAQS.map((faq, index) => (
                <FaqItem key={index} q={faq.q} a={faq.a} i={index} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-[#F8FAFC]">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-2 items-center">
          <Reveal from="left">
            <div className="relative w-full h-[220px] sm:h-[300px] lg:h-[380px] overflow-hidden rounded-[22px]">
              <Image
                src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&q=80"
                alt="Talk to specialist"
                fill
                className="object-cover"
              />
            </div>
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div>
              <span className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0] mb-3.5">
                Ready to Secure Business Financing?
              </span>
              <Divider />
              <h2 className="text-[clamp(24px,3vw,38px)] font-bold leading-[1.18] mb-6">
                Connect With Our Loan Specialists
              </h2>
              <p className="text-[15px] leading-[1.85] text-[#64748B] mb-4">
                Free eligibility assessment, no paperwork hassle, expert negotiation on your behalf.
              </p>
              <p className="text-[15px] leading-[1.85] text-[#64748B] mb-9">
                We charge no upfront fees — our facilitation fee applies only upon successful loan disbursement.
              </p>
              <div className="flex flex-wrap gap-3">
                <button className="inline-flex items-center justify-center rounded-sm bg-[#1565C0] text-white px-8 py-3 text-[13px] font-bold tracking-[0.06em]">
                  CHECK LOAN ELIGIBILITY
                </button>
                <button className="inline-flex items-center justify-center rounded-sm border border-[#E2E8F0] bg-transparent text-[#0F172A] px-8 py-3 text-[13px] font-semibold tracking-[0.06em]">
                  TALK TO A SPECIALIST
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
